import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "../Button";
import { Reveal } from "../Reveal";
import { trackEvent } from "../../lib/analytics";
import {
  IT_PLANS,
  PLAN_COMPARISON,
  PRICING_DISCLOSURE,
  BACKUP_FOOTNOTE,
  SNAPSHOT_CREDIT_OFFER,
} from "../../data/itSupport";

const trackCompare = (label, pagePath) =>
  trackEvent("plan_compare_interaction", { cta_label: label, page_path: pagePath });

export const PlanCards = ({ pagePath, compact = false }) => (
  <div className="grid md:grid-cols-3 gap-6 items-stretch">
    {IT_PLANS.map((p, i) => (
      <Reveal key={p.slug} delay={i * 0.07} className="h-full">
        <div
          className={`relative flex flex-col h-full rounded-2xl border bg-white ${compact ? "p-6" : "p-7 lg:p-8"} ${
            p.popular
              ? "border-forest ring-2 ring-forest shadow-[0_16px_50px_rgb(35,79,61,0.12)]"
              : "border-silver/40"
          }`}
          data-testid={`plan-card-${p.slug}`}
        >
          {p.popular && (
            <span className="absolute -top-3 left-6 rounded-full bg-gold text-charcoal text-xs font-bold px-3 py-1 shadow-sm">
              Most Popular
            </span>
          )}
          <h3 className="text-lg font-bold text-charcoal">{p.name}</h3>
          <p className="mt-1 text-sm font-medium text-deepblue">{p.headline}</p>
          <p className="mt-4 text-charcoal">
            <span className="text-3xl font-bold text-forest">{p.price}</span>
            <span className="text-sm font-medium text-charcoal/60">/month</span>
          </p>
          <p className="mt-1 text-sm font-semibold text-charcoal/75" data-testid={`plan-included-${p.slug}`}>
            {p.included}
          </p>
          <p className="mt-0.5 text-xs font-medium text-charcoal/60" data-testid={`plan-extra-${p.slug}`}>
            {p.extra}
          </p>
          {!compact && (
            <ul className="mt-5 space-y-2.5 text-sm text-charcoal/75 flex-1">
              {p.inclusions.map((inc) => (
                <li key={inc} className="flex items-start gap-2.5">
                  <Check className="h-4 w-4 text-forest shrink-0 mt-0.5" /> {inc}
                </li>
              ))}
            </ul>
          )}
          {compact && <p className="mt-4 text-sm text-charcoal/70 leading-relaxed flex-1">{p.message}</p>}
          <div className="mt-6">
            <Button
              to={`/it-support/${p.slug}`}
              variant={p.popular ? "primary" : "secondary"}
              className="w-full"
              data-testid={`plan-cta-${p.slug}`}
              onClick={() => trackCompare(p.cta, pagePath)}
            >
              {p.cta} <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
          {!compact && (p.slug === "managed" || p.slug === "secure") && (
            <p className="mt-4 text-xs text-charcoal/70 leading-relaxed">{SNAPSHOT_CREDIT_OFFER}</p>
          )}
        </div>
      </Reveal>
    ))}
  </div>
);

export const PlanComparisonTable = ({ pagePath }) => (
  <div className="mt-12">
    <Reveal>
      <div
        className="bg-white rounded-2xl border border-silver/40 overflow-x-auto"
        data-testid="plan-comparison-table"
        onClick={() => trackCompare("comparison_table", pagePath)}
      >
        <table className="w-full text-sm">
          <caption className="sr-only">Comparison of Silvertree Assist, Managed, and Secure plan inclusions</caption>
          <thead>
            <tr className="border-b border-silver/40 bg-[#FAFBFB]">
              <th scope="col" className="text-left px-5 py-4 font-semibold text-charcoal">Capability</th>
              {IT_PLANS.map((p) => (
                <th key={p.slug} scope="col" className="text-left px-5 py-4 font-semibold text-charcoal whitespace-nowrap">
                  {p.short}
                  {p.popular && <span className="ml-2 text-[10px] font-bold uppercase text-[#8a6a1f]">Most Popular</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PLAN_COMPARISON.map((row) => (
              <tr key={row.capability} className="border-b border-silver/20 last:border-0">
                <th scope="row" className="text-left px-5 py-3.5 font-medium text-charcoal/85">{row.capability}</th>
                {["assist", "managed", "secure"].map((k) => (
                  <td key={k} className="px-5 py-3.5 text-charcoal/75">
                    {row[k] === "Included" ? (
                      <span className="inline-flex items-center gap-1.5 text-forest font-medium">
                        <Check className="h-4 w-4" /> Included
                      </span>
                    ) : (
                      row[k]
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Reveal>
    <p className="mt-4 text-xs text-charcoal/70 max-w-3xl">{BACKUP_FOOTNOTE}</p>
    <p className="mt-2 text-sm text-charcoal/75 max-w-3xl font-medium" data-testid="pricing-disclosure">
      {PRICING_DISCLOSURE}
    </p>
    <p className="mt-3 text-sm text-charcoal/70">
      Not sure which fits?{" "}
      <Link to="/business-it-support#it-support-form" className="text-forest font-medium hover:underline">
        Schedule a 15-minute technology check
      </Link>{" "}
      and we'll tell you honestly — even if the answer is the cheaper plan.
    </p>
  </div>
);
